/* eslint-disable no-nested-ternary */
import { OutboundProcess } from '@features/supplyChain/types';
import { useReactHookForm } from '@hooks';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';
import { PalletCalculator } from '@components/PalletCalculator';
import { ProductLinesSection } from '../../../views/Noticeboard/components/ProductLinesSection';
import { AuthoriseForPickingFormValues } from './types';
import { makeDefaultValues, validationSchema } from './form';
import { Datepicker } from '../../../tailwindComponents/Datepicker';
import { Button } from '../../../tailwindComponents/Button';

interface AuthoriseForPickingFormProps {
  selectedOutboundProcess: OutboundProcess;
  onClose: () => void;
  onSubmit: (values: AuthoriseForPickingFormValues) => void;
  isDesktop: boolean;
  isTablet: boolean;
}

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <line x1='15' x2='9' y1='9' y2='15' />
      <line x1='9' x2='15' y1='9' y2='15' />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <polyline points='20 6 9 17 4 12' />
    </svg>
  );
}

export function AuthoriseForPickingForm({
  selectedOutboundProcess,
  onClose,
  onSubmit,
  isDesktop,
  isTablet,
}: AuthoriseForPickingFormProps) {
  const { handleSubmit, isSubmitting, isValid, control, setValue } =
    useReactHookForm<AuthoriseForPickingFormValues>({
      defaultValues: makeDefaultValues(selectedOutboundProcess),
      validationSchema,
    });

  if (!selectedOutboundProcess || !selectedOutboundProcess.despatchNote) {
    return null;
  }

  const { despatchNote } = selectedOutboundProcess;
  const pickingNoteNumber = despatchNote.despatchNoteNumber.replace(
    '-DN',
    '-P'
  );

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(pickingNoteNumber);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Picking note #{pickingNoteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy picking note number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Shipper name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Shipper name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {selectedOutboundProcess.userBusinessName}
          </span>
        </div>

        {/* Recipient name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Recipient name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {despatchNote.customer?.name ?? despatchNote.customerName ?? 'N/A'}
          </span>
        </div>

        {/* Creation date */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Creation date</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(despatchNote.createdAt).format('D MMM YYYY')}
          </span>
        </div>
      </div>

      {/* Additional Info Row */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Required ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Required ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {`${dayjs(despatchNote.requiredTimeOfArrival.dateTime)
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('D MMM YYYY')}, by ${dayjs(
              despatchNote.requiredTimeOfArrival.dateTime
            )
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('HH:mm')}`}
          </span>
        </div>

        {/* Picking Date - Editable */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500 mb-1'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Picking date</span>
          </div>
          <Controller
            name='pickingDateConfirmation'
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <Datepicker
                  name={field.name}
                  value={
                    field.value
                      ? dayjs(field.value).format('YYYY-MM-DD')
                      : dayjs().format('YYYY-MM-DD')
                  }
                  onChange={(e) => {
                    setValue(
                      'pickingDateConfirmation',
                      dayjs(e.target.value).toISOString(),
                      {
                        shouldValidate: true,
                      }
                    );
                  }}
                  startDate={dayjs().format('YYYY-MM-DD')}
                  isInvalid={!!error}
                  validationMessage={error?.message}
                />
              );
            }}
          />
        </div>
      </div>

      {/* SKU Table */}
      <div className='px-6 py-4'>
        <h3 className='text-sm font-semibold text-gray-900 mb-4'>Items</h3>

        {/* Table Header - Desktop only */}
        {despatchNote.skus.length > 0 && isDesktop && (
          <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1.5fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
            <span className='text-xs font-semibold text-gray-500 text-center'>
              #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              SKU name
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Quantity
            </span>
            <span className='text-xs font-semibold text-gray-500'>Pallets</span>
            <span className='text-xs font-semibold text-gray-500'>
              Pallet info
            </span>
          </div>
        )}

        {/* SKU Rows */}
        {despatchNote.skus.length > 0 && (
        <div className='flex flex-col gap-4'>
          {despatchNote.skus.map((sku, index) => (
            <div
              key={sku.skuId}
              className={`${
                index !== despatchNote.skus.length - 1
                  ? 'pb-4 border-b border-gray-100'
                  : ''
              }`}
            >
              {isDesktop ? (
                /* Desktop Layout */
                <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1.5fr] gap-4 items-start'>
                  {/* Index */}
                  <div className='flex justify-center'>
                    <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                      {index + 1}
                    </span>
                  </div>
                  {/* SKU ID */}
                  <span className='text-sm text-gray-900'>
                    {sku.skuIdentifier}
                  </span>
                  {/* SKU name */}
                  <span className='text-sm text-gray-900'>{sku.name}</span>
                  {/* Quantity */}
                  <span className='text-sm text-gray-900'>
                    {sku.skuQuantity}
                  </span>
                  {/* Pallets */}
                  <div className='text-sm text-gray-900'>
                    {sku.packagingDetails.packagingInfo ? (
                      <PalletCalculator
                        palletName={sku.packagingDetails.packagingInfo.type}
                        skuQuantity={sku.skuQuantity}
                        amountOfSkusInLayer={
                          sku.packagingDetails.packagingInfo.amountOfSkusInLayer
                        }
                        amountOfLayersInPallet={
                          sku.packagingDetails.packagingInfo
                            .amountOfLayersInPallet
                        }
                      />
                    ) : (
                      <span>{sku.packagingDetails.palletisingType}</span>
                    )}
                  </div>
                  {/* Pallet description */}
                  <span className='text-sm text-gray-600'>
                    {sku.packagingDetails.palletisingDescription}
                  </span>
                </div>
              ) : (
                /* Mobile/Tablet Layout */
                <div className='flex gap-3'>
                  {/* Index */}
                  <div className='flex-shrink-0'>
                    <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                      {index + 1}
                    </span>
                  </div>
                  {/* Details */}
                  <div
                    className={`flex-1 grid gap-3 ${
                      isTablet ? 'grid-cols-2' : 'grid-cols-1'
                    }`}
                  >
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        SKU ID
                      </span>
                      <span className='text-sm text-gray-900'>
                        {sku.skuIdentifier}
                      </span>
                    </div>
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        SKU name
                      </span>
                      <span className='text-sm text-gray-900'>{sku.name}</span>
                    </div>
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        Quantity
                      </span>
                      <span className='text-sm text-gray-900'>
                        {sku.skuQuantity}
                      </span>
                    </div>
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        Pallets
                      </span>
                      <div className='text-sm text-gray-900'>
                        {sku.packagingDetails.packagingInfo ? (
                          <PalletCalculator
                            palletName={sku.packagingDetails.packagingInfo.type}
                            skuQuantity={sku.skuQuantity}
                            amountOfSkusInLayer={
                              sku.packagingDetails.packagingInfo
                                .amountOfSkusInLayer
                            }
                            amountOfLayersInPallet={
                              sku.packagingDetails.packagingInfo
                                .amountOfLayersInPallet
                            }
                          />
                        ) : (
                          <span>{sku.packagingDetails.palletisingType}</span>
                        )}
                      </div>
                    </div>
                    {sku.packagingDetails.palletisingDescription && (
                      <div
                        className={`flex flex-col gap-0.5 ${
                          isTablet ? 'col-span-2' : ''
                        }`}
                      >
                        <span className='text-xs font-medium text-gray-500'>
                          Pallet description
                        </span>
                        <span className='text-sm text-gray-600'>
                          {sku.packagingDetails.palletisingDescription}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {(despatchNote.products?.length ?? 0) > 0 && (
          <div className={despatchNote.skus.length > 0 ? 'mt-6' : ''}>
            <h4 className='text-sm font-semibold text-gray-900 mb-4'>
              Products
            </h4>

            <ProductLinesSection
              products={despatchNote.products ?? []}
              isDesktop={isDesktop}
              isTablet={isTablet}
            />
          </div>
        )}
      </div>

      {/* Comments Section */}
      {despatchNote.comment && (
        <div className='px-6 py-4 border-t border-gray-200'>
          <h3 className='text-sm font-semibold text-gray-900 mb-2'>Comment</h3>
          <p className='text-sm text-gray-600'>{despatchNote.comment}</p>
        </div>
      )}

      {/* Footer Actions */}
      <div
        className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
          isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
        }`}
      >
        <Button
          label='Discard'
          intent='secondary'
          size='sm'
          onClick={onClose}
          disabled={isSubmitting}
          startIcon={<CloseIcon />}
          className={!isDesktop ? 'w-full' : ''}
          type='button'
        />
        <Button
          label={isSubmitting ? 'Authorising...' : 'Authorise For Picking'}
          intent='primary'
          size='sm'
          type='submit'
          disabled={isSubmitting || !isValid}
          startIcon={<CheckIcon />}
          className={!isDesktop ? 'w-full' : ''}
        />
      </div>
    </form>
  );
}
